import React, { useState, useCallback, useEffect, useMemo } from "react";

import { Link } from "gatsby";

import getFormattedDate from "sites-common/utils/FormatDate";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faArrowRight,
  faCalendarAlt,
} from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { lowerCase } from "sites-common/utils/lodash";
import { Select, Label, Checkbox } from "theme-ui";
import Loader from "react-loader-spinner";
import EventsListingBanner from "../components/events-listing/banner/index";
import Layout from "../layout/index";
import Seo from "../components/seo/index";
import sampleImg from "../assets/images/sample-temp-bg.png";

const getMandatoryEnv = require("sites-common/utils/getMandatoryEnv");

const { kanhaStaticConfig } = getMandatoryEnv(["kanhaStaticConfig"]);

const shortDate = "shortDate";
const initalFilterVal = {
  status: "All",
  type: "All",
};

const itemsPerPage = 9;

const viewMoreLink = (link) => {
  const isExternal = link.includes("http");
  if (isExternal) {
    return (
      <a
        href={link}
        target="_blank"
        rel="noreferrer noopener"
        className="text-decoration-none"
      >
        View More
      </a>
    );
  }
  return (
    <Link to={link} className="text-decoration-none">
      View More
    </Link>
  );
};

const EventsListing = () => {
  const [eventsData, setEventsData] = useState({});
  const [filter, setFilter] = useState(initalFilterVal);
  const [events, setEvents] = useState([]);
  const [searchedEvents, setSearchedEvents] = useState([]);
  const [showFilter, setShowFilter] = useState(false);
  const [topFilter, setTopFilter] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showRegistrationsOpen, setShowRegistrationsOpen] = useState(false);
  const [registrationsOpen, setRegistrationsOpen] = useState(false);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${kanhaStaticConfig}/events.json`)
      .then((res) => {
        setEventsData(res?.data);
        setEvents(res?.data?.events);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, []);

  const totalcount = useMemo(() => events?.length, [events]);

  const filteredEvents = useMemo(() => {
    const sortEventsByFrequency = (localEvents) => {
      const sunDayEventsData = localEvents?.filter((data) => {
        if (data?.recurring && data?.frequency === "SUNDAY") {
          return data;
        }
        return "";
      });
      sunDayEventsData.sort((a, b) => {
        return parseFloat(b.id) - parseFloat(a.id);
      });
      const dailyEventsData = localEvents?.filter((data) => {
        if (data?.recurring && data?.frequency === "DAILY") {
          return data;
        }
        return "";
      });
      dailyEventsData.sort((a, b) => {
        return parseFloat(b.id) - parseFloat(a.id);
      });
      const monSatDaysEventsData = localEvents?.filter((data) => {
        if (data?.recurring && data?.frequency === "MON-SAT") {
          return data;
        }
        return "";
      });
      monSatDaysEventsData.sort((a, b) => {
        return parseFloat(b.id) - parseFloat(a.id);
      });
      const otherFrequentEvents = localEvents?.filter((data) => {
        if (
          data?.frequency !== "SUNDAY" &&
          data?.frequency !== "DAILY" &&
          data?.frequency !== "MON-SAT" &&
          data?.frequency !== ""
        ) {
          return data;
        }
        return "";
      });
      otherFrequentEvents.sort((a, b) => {
        return parseFloat(b.id) - parseFloat(a.id);
      });
      const allDaysEventsData = localEvents?.filter((data) => {
        if (
          data?.frequency !== "SUNDAY" &&
          data?.frequency !== "DAILY" &&
          data?.frequency !== "MON-SAT" &&
          data?.frequency === "" &&
          !data?.recurring
        ) {
          return data;
        }
        return "";
      });

      if (
        filter?.status === "Ongoing" ||
        filter?.status === "Coming soon" ||
        (searchedEvents.length && topFilter)
      ) {
        allDaysEventsData.sort(
          (a, b) =>
            new Date(a.start_date).getTime() - new Date(b.start_date).getTime()
        );
      } else {
        allDaysEventsData.sort(
          (a, b) =>
            new Date(b.start_date).getTime() - new Date(a.start_date).getTime()
        );
      }

      const allEventsDays = [
        ...sunDayEventsData,
        ...dailyEventsData,
        ...monSatDaysEventsData,
        ...otherFrequentEvents,
        ...allDaysEventsData,
      ];
      return allEventsDays;
    };

    const filterSortEvents = sortEventsByFrequency(events);
    return filterSortEvents?.slice(
      (page - 1) * itemsPerPage,
      (page + itemsPerPage - 1 - (page - 1)) * page
    );
  }, [events, filter?.status, page, searchedEvents.length, topFilter]);

  useEffect(() => {
    if (loading) {
      setTimeout(() => setLoading(false), 500);
    }
  }, [loading]);

  useEffect(() => {
    let ev = [];
    if (searchedEvents.length && topFilter) {
      ev = [...searchedEvents];
    } else if (eventsData?.events && !topFilter) {
      ev = [...eventsData?.events];
    }
    if (filter?.status && filter?.status !== "All" && ev.length) {
      if (filter?.status === "Ongoing") {
        ev = ev.filter(
          (a) =>
            new Date() >= new Date(a.start_date) &&
            new Date() <= new Date(a.end_date)
        );
      }
      if (filter?.status === "Coming soon") {
        setShowRegistrationsOpen(true);
        ev = ev.filter(
          (a) =>
            new Date() <= new Date(a.start_date) &&
            new Date() <= new Date(a.end_date)
        );
        if (registrationsOpen) {
          ev = ev.filter(
            (a) =>
              new Date() >= new Date(a.reg_start_date) &&
              new Date() <= new Date(a.reg_end_date)
          );
        }
      }
      if (filter?.status === "Closed") {
        ev = ev.filter(
          (a) =>
            new Date() >= new Date(a.start_date) &&
            new Date() >= new Date(a.end_date)
        );
      }
    }
    if (filter?.status && filter?.status !== "Coming soon") {
      setShowRegistrationsOpen(false);
    }
    if (filter?.type && filter?.type !== "All") {
      ev = ev.filter((a) => a.type === filter.type);
    }
    const filterLang = Object.entries(filter?.language || {})
      .filter(([, v]) => v)
      .map(([k]) => k);

    if (filterLang && filterLang.length) {
      ev = ev.filter((a) => {
        const curlan = a?.language || [];
        let contains = false;
        if (
          curlan &&
          (typeof curlan === "string" || curlan instanceof String)
        ) {
          if (filterLang.includes(lowerCase(curlan))) {
            contains = true;
          }
          // return null;
        } else if (curlan && curlan.length) {
          curlan.map((f) => {
            if (filterLang.includes(lowerCase(f))) {
              contains = true;
            }
            return null;
          });
        }
        return contains;
      });
    }
    setLoading(true);
    setPage(1);

    setEvents(ev);
  }, [
    eventsData?.events,
    filter,
    registrationsOpen,
    searchedEvents,
    topFilter,
  ]);

  const findEventBy = useCallback(
    (fData) => {
      const searchedEventsByForm = (formData) => {
        let ev = [...eventsData?.events];
        if (formData?.eventName) {
          ev = ev.filter((a) =>
            lowerCase(a.name).includes(lowerCase(formData.eventName))
          );
        }
        let endDate = "";
        if (formData?.startDate && formData?.endDate) {
          endDate = new Date(formData?.endDate);
          endDate.setHours(endDate.getHours() + 23);
          ev = ev.filter(
            (a) =>
              new Date(a.start_date) >= new Date(formData.startDate) &&
              new Date(a.end_date) <= new Date(endDate)
          );
        } else if (formData?.startDate) {
          ev = ev.filter(
            (a) => new Date(a.start_date) >= new Date(formData.startDate)
          );
        } else if (formData.endDate) {
          endDate = new Date(formData?.endDate);
          endDate.setHours(endDate.getHours() + 23);
          ev = ev.filter((a) => new Date(a.end_date) <= new Date(endDate));
        }
        setTopFilter(true);
        return ev;
      };

      const resultsEvents = searchedEventsByForm(fData);
      setSearchedEvents(resultsEvents);
      setFilter(initalFilterVal);
      setEvents(resultsEvents);
    },
    [eventsData?.events]
  );

  const clearTopFilter = useCallback(() => {
    setEvents(eventsData?.events);
    setSearchedEvents([]);
    setTopFilter(false);
    setLoading(true);
    setFilter(initalFilterVal);
  }, [eventsData?.events]);

  const registrationsOpenChange = (e) => {
    setRegistrationsOpen(e.target.checked);
  };

  return (
    <>
      <Seo title="Events Listing" description="Kanha " />
      <Layout isSticky wrapperClass="home">
        <div className="book-a-stay">
          <EventsListingBanner
            findEventBy={findEventBy}
            clearFields={!topFilter}
            setPage={setPage}
          />
          <div className="events-listing-wr">
            <div className="container my-5">
              <div className="row">
                <div className="col-12">
                  <div className="mobile-filter-wrapper">
                    <h3>Events</h3>
                    <button
                      type="button"
                      className="filter-btn"
                      onClick={() => setShowFilter((prev) => !prev)}
                    >
                      Filter
                    </button>
                    {topFilter && (
                      <button
                        type="button"
                        className="filter-btn"
                        onClick={clearTopFilter}
                      >
                        Clear Filter
                      </button>
                    )}
                  </div>
                </div>
                <div
                  className={`col-12 col-lg-3 filter-section ${
                    showFilter ? "" : "hide"
                  }`}
                >
                  <h3>Filters</h3>
                  <h6 className="first_title">Event Status</h6>
                  <Select
                    name="status"
                    id="status"
                    defaultValue="All"
                    className="cust_dropdown"
                    value={filter?.status}
                    onChange={(e) =>
                      setFilter((prev) => ({ ...prev, status: e.target.value }))
                    }
                  >
                    <option value="All">All</option>
                    <option value="Ongoing">Ongoing</option>
                    <option value="Coming soon">Coming soon</option>
                    <option value="Closed">Closed</option>
                  </Select>
                  <br />
                  {showRegistrationsOpen ? (
                    <>
                      <Label>
                        <Checkbox
                          defaultChecked={false}
                          onChange={(e) => registrationsOpenChange(e)}
                          // style={{ color: "#1c699f" }}
                        />{" "}
                        Registrations Open
                      </Label>
                      <br />
                    </>
                  ) : null}
                  <h6>Language</h6>
                  <div>
                    <button
                      type="button"
                      className={`lang_select ${
                        filter?.language?.hindi ? "active" : ""
                      }`}
                      onClick={() =>
                        setFilter((prev) => ({
                          ...prev,
                          language: {
                            ...(prev.language || {}),
                            hindi: !prev?.language?.hindi,
                          },
                        }))
                      }
                    >
                      Hindi
                    </button>
                    <button
                      type="button"
                      className={`lang_select ${
                        filter?.language?.english ? "active" : ""
                      }`}
                      onClick={() =>
                        setFilter((prev) => ({
                          ...prev,
                          language: {
                            ...(prev.language || {}),
                            english: !prev?.language?.english,
                          },
                        }))
                      }
                    >
                      English
                    </button>
                    <button
                      type="button"
                      className={`lang_select ${
                        filter?.language?.telugu ? "active" : ""
                      }`}
                      onClick={() =>
                        setFilter((prev) => ({
                          ...prev,
                          language: {
                            ...(prev.language || {}),
                            telugu: !prev?.language?.telugu,
                          },
                        }))
                      }
                    >
                      Telugu
                    </button>
                  </div>
                  <br />
                  <h6>Event Type</h6>
                  <Select
                    name="type"
                    id="type"
                    defaultValue="All"
                    className="cust_dropdown"
                    value={filter?.type}
                    onChange={(e) =>
                      setFilter((prev) => ({ ...prev, type: e.target.value }))
                    }
                  >
                    <option value="All">All</option>
                    <option value="Free">Free</option>
                    <option value="Paid">Paid</option>
                  </Select>
                  <br />
                </div>
                <div className="col-lg-9">
                  <div className="row">
                    <div className="col-12 desktop-view">
                      <h3>Events</h3>
                      {topFilter && (
                        <button
                          className="filter-btn"
                          type="button"
                          onClick={clearTopFilter}
                        >
                          Clear Filter
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-12">
                      <div className="row">
                        {!loading && filteredEvents?.length === 0 ? (
                          <p className="my-3 text-secondary">
                            No events available for your search.
                          </p>
                        ) : (
                          ""
                        )}
                        {loading ? (
                          <div className="cus_loading">
                            <div className="d-flex">
                              <Loader
                                type="Oval"
                                color="#74748a"
                                width={30}
                                height={25}
                              />
                              <span>&nbsp;Loading...</span>
                            </div>
                          </div>
                        ) : (
                          filteredEvents?.map((item) => {
                            return (
                              <div
                                className="col-lg-4 col-md-6 col-sm-12 event_card_wrapper"
                                key={item?.id}
                              >
                                <div className="event-card-wr my-3">
                                  <div className="img_wrapper">
                                    <img
                                      src={item?.image_link || sampleImg}
                                      alt={item?.name}
                                    />
                                  </div>
                                  <div className="info-wr p-3">
                                    <strong title={item?.name}>
                                      {item?.name}
                                    </strong>
                                    <p className="mb-0">
                                      {item?.category || "-"}
                                    </p>
                                    <div className="my-2 date_container">
                                      <FontAwesomeIcon
                                        icon={faCalendarAlt}
                                        color="#b0853a"
                                        width="20px"
                                        className="me-2"
                                        height="20px"
                                      />
                                      {item?.start_date !== "" &&
                                        getFormattedDate(
                                          item?.start_date,
                                          shortDate
                                        )}{" "}
                                      {item?.end_date !== "" && "-"}{" "}
                                      {item?.end_date !== "" &&
                                        getFormattedDate(
                                          item?.end_date,
                                          shortDate
                                        )}
                                      {item?.end_date === "" &&
                                        item?.start_date === "" &&
                                        "-"}
                                    </div>
                                    <p className="">
                                      <span>Venue:</span> {item?.venue || "-"}
                                    </p>
                                    <p className="desc_wrap">
                                      {item?.description}
                                    </p>
                                    {item?.navigation_link ? (
                                      viewMoreLink(item?.navigation_link)
                                    ) : (
                                      <span>&nbsp;</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                  </div>
                  {totalcount !== 0 ? (
                    <div className="cus_pagination">
                      <button
                        type="button"
                        disabled={page === 1}
                        onClick={() => {
                          window.scrollTo({
                            top: 0,
                            behavior: "smooth",
                          });
                          setLoading(true);
                          setPage(page - 1);
                        }}
                      >
                        <FontAwesomeIcon icon={faArrowLeft} />
                      </button>
                      <button
                        type="button"
                        // disabled={page === Math.ceil(totalcount / itemsPerPage)}
                        onClick={() => {
                          window.scrollTo({
                            top: 0,
                            behavior: "smooth",
                          });
                          setLoading(true);
                          setPage(page + 1);
                        }}
                      >
                        <FontAwesomeIcon icon={faArrowRight} />
                      </button>
                    </div>
                  ) : (
                    ""
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    </>
  );
};

// EventsListing.propTypes = {
//   data: PropTypes.any.isRequired,
// };

export default EventsListing;
